/**
 * Offline axios interceptor — auto-caches every /api GET and falls back to the
 * cache when the network is unreachable or the response errors out.
 *
 * Behaviour:
 *   • On every successful /api GET, save the response body to IndexedDB
 *     keyed by the URL (query string included).
 *   • On every failed /api GET (network error, 5xx), try to return the cached
 *     response with a synthetic header marking it stale.
 *   • Non-GET requests pass through untouched — mutations are handled by the
 *     DailyCheckIn flow via `enqueueMutation`.
 *
 * Mounted once from index.js so it catches every axios call in the app.
 */
import axios from "axios";
import { saveSnapshot, getSnapshot } from "./offlineCache";

const API = process.env.REACT_APP_BACKEND_URL;
const CACHE_PREFIX = "api:";

function keyFor(config) {
  // URL may be absolute or relative — normalise.
  const url = (config.url || "").replace(API || "", "");
  const params = config.params
    ? "?" + new URLSearchParams(config.params).toString()
    : "";
  return CACHE_PREFIX + url + params;
}

// Endpoints that are mutating writes dressed up as GETs (none today) or are
// noisy enough to skip caching — keep this list short.
const SKIP_CACHE_PATHS = [
  "/api/metrics/event",  // write
  "/api/auth/logout",    // destructive
];

function shouldCache(config) {
  if ((config.method || "get").toLowerCase() !== "get") return false;
  const path = (config.url || "").replace(API || "", "");
  if (!path.startsWith("/api/")) return false;
  return !SKIP_CACHE_PATHS.some((p) => path.startsWith(p));
}

export function installOfflineAxiosInterceptor() {
  axios.interceptors.response.use(
    async (response) => {
      try {
        if (shouldCache(response.config)) {
          // Fire-and-forget — don't await to avoid slowing the UI.
          saveSnapshot(keyFor(response.config), response.data);
        }

        // One successful write can change several operational surfaces
        // (Today, calendar, pupil profile, money). Tell mounted consumers
        // to revalidate instead of leaving them stale until navigation.
        const method = (response.config?.method || "get").toLowerCase();
        const path = (response.config?.url || "").replace(API || "", "");
        if (
          ["post", "put", "patch", "delete"].includes(method)
          && path.startsWith("/api/")
          && typeof window !== "undefined"
        ) {
          window.dispatchEvent(new CustomEvent("ndg:platform-refresh", {
            detail: { method, path },
          }));
        }
      } catch { /* ignore */ }
      return response;
    },
    async (error) => {
      const config = error?.config || {};
      if (!shouldCache(config)) return Promise.reject(error);

      // Network unreachable or 5xx — try the cache.
      const offline = typeof navigator !== "undefined" && !navigator.onLine;
      const serverDown = !error.response || (error.response.status >= 500);
      if (!offline && !serverDown) return Promise.reject(error);

      const cached = await getSnapshot(keyFor(config));
      if (cached) {
        return Promise.resolve({
          data: cached.data,
          status: 200,
          statusText: "(from cache)",
          headers: { "x-from-cache": "true", "x-synced-at": cached.syncedAt },
          config,
          request: null,
          fromCache: true,
        });
      }
      return Promise.reject(error);
    }
  );
}
